import { apiFetch, ApiClientError } from './client.js';

export interface AuthUser {
  id: string;
  email: string;
  username: string;
  name: string;
  avatarUrl?: string | null;
}

/** Empresa de la licencia (`company`) o equipo de eko27 (`team`). */
export type TenantKind = 'company' | 'team';

export interface AuthMembership {
  id: string;
  slug: string;
  name: string;
  status: string;
  role: 'owner' | 'admin' | 'member' | 'viewer' | 'staff';
  /** True si este tenant es el de Ecosoft (gate para nav "Admin" + eCostos). */
  isEcosoftOwned?: boolean;
  /** Ausente en respuestas viejas → se trata como `company`. */
  kind?: TenantKind;
}

export interface AuthProgram {
  id: string;
  programId: string;
  status: string;
  features: Record<string, unknown>;
  seats: number;
  startedAt: string | null;
  expiresAt: string | null;
}

export interface AuthBrief {
  /** Ausente en multi-empresa federada con Anywhere (el Guid solo se conoce
   *  después de elegir empresa y volver a llamar /auth/login con tenantSlug). */
  id?: string;
  slug: string;
  name: string;
  /** Ausente en multi-empresa federada (igual que id). */
  role?: string;
  /** Ausente en multi-empresa federada y en respuestas viejas → `company`. */
  kind?: TenantKind;
}

export interface LoginSuccess {
  user: AuthUser;
  tenant: AuthMembership;
  programs: AuthProgram[];
  availableTenants?: AuthBrief[];
  expiresInSeconds?: number;
  /** Fecha (ISO) de caducidad de la suscripción OPUS 27 según Anywhere. */
  subscriptionExpiresAt?: string | null;
  /** True si la fecha ya pasó. m0 entra en modo lectura. */
  subscriptionExpired?: boolean;
  /** True sólo en entornos de pruebas para admin/owner ECOSOFT: habilita
   *  el modal post-login que pregunta si operar como vencido o tester. */
  qaModeEnabled?: boolean;
  /** Si esta persona ve la actividad de usuarios. El servidor manda el sí/no, nunca la lista. */
  puedeVerActividad?: boolean;
  /** Sesión del sitio demo público (otra URL): solo lectura. */
  demoGuest?: boolean;
  /** CTA al producto real cuando demoGuest. */
  productUrl?: string;
}

export async function qaBypassSubscription(
  bypass: boolean,
): Promise<{ ok: boolean; bypassed: boolean; subscriptionExpired: boolean }> {
  return apiFetch('/api/v1/auth/qa-bypass-subscription', {
    method: 'POST',
    body: { bypass },
  });
}

export interface MultiTenantPrompt {
  code: 'MULTI_TENANT_USER';
  message: string;
  tenants: AuthBrief[];
}

/** El usuario tiene licencia OPUS pero NO un plan de IA activo: hay que comprar
 *  Start en el hub (apps.opus.com.mx). El login NO inicia sesión en este caso. */
export interface NoPlanPrompt {
  code: 'NO_PLAN';
  message: string;
  hubUrl: string;
}

/** Hace falta el código de verificación que se mandó por correo (equipo nuevo o staff). */
export interface OtpPrompt {
  code: 'OTP_REQUIRED';
  message: string;
  desafio: string;
  destino: string;
  motivo: 'dispositivo_nuevo' | 'staff';
}

export type LoginResult =
  | { kind: 'success'; data: LoginSuccess }
  | { kind: 'multiTenant'; data: MultiTenantPrompt }
  | { kind: 'noPlan'; data: NoPlanPrompt }
  | { kind: 'otp'; data: OtpPrompt };

export async function login(args: {
  username: string;
  password: string;
  tenantSlug?: string;
}): Promise<LoginResult> {
  try {
    const body = await apiFetch<LoginSuccess | MultiTenantPrompt | NoPlanPrompt | OtpPrompt>('/api/v1/auth/login', {
      method: 'POST',
      body: args,
      acceptStatus: [300, 402],
    });
    if ((body as NoPlanPrompt).code === 'NO_PLAN') {
      // Sin plan de IA: redirigir al hub a comprar Start (el mismo login lo manda).
      const hubUrl = (body as NoPlanPrompt).hubUrl || 'https://apps.opus.com.mx';
      if (typeof window !== 'undefined') window.location.href = hubUrl;
      return { kind: 'noPlan', data: body as NoPlanPrompt };
    }
    if ((body as MultiTenantPrompt).code === 'MULTI_TENANT_USER') {
      return { kind: 'multiTenant', data: body as MultiTenantPrompt };
    }
    if ((body as OtpPrompt).code === 'OTP_REQUIRED') {
      return { kind: 'otp', data: body as OtpPrompt };
    }
    return { kind: 'success', data: body as LoginSuccess };
  } catch (err) {
    if (err instanceof ApiClientError) throw err;
    throw new ApiClientError(0, { code: 'NETWORK', message: 'Sin conexión al servidor' });
  }
}

export async function me(): Promise<LoginSuccess> {
  return await apiFetch<LoginSuccess>('/api/v1/auth/me');
}

/** Entrada al sitio demo público tras registro de lead (nombre + correo). */
export async function enterDemoGuest(lead: { name: string; email: string }): Promise<LoginSuccess> {
  return await apiFetch<LoginSuccess>('/api/v1/auth/demo-guest', {
    method: 'POST',
    body: { name: lead.name, email: lead.email },
  });
}

export async function logout(): Promise<void> {
  await apiFetch('/api/v1/auth/logout', { method: 'POST' });
}

/**
 * Segundo paso de la entrada: verifica el código. Devuelve la sesión (login / cambio de
 * empresa) o, en las entradas desde OPUS 27 o el hub, a dónde ir.
 */
export async function verificarCodigo(
  desafio: string,
  codigo: string,
  recordar: boolean,
): Promise<LoginSuccess | { ok: true; redirect: string }> {
  return await apiFetch('/api/v1/auth/otp/verificar', {
    method: 'POST',
    body: { desafio, codigo, recordar },
  });
}

export async function reenviarCodigo(desafio: string): Promise<{ ok: boolean; message: string }> {
  return await apiFetch('/api/v1/auth/otp/reenviar', { method: 'POST', body: { desafio } });
}

export async function switchTenant(tenantSlug: string): Promise<LoginSuccess | OtpPrompt> {
  return await apiFetch<LoginSuccess | OtpPrompt>('/api/v1/auth/switch-tenant', {
    method: 'POST',
    body: { tenantSlug },
  });
}

export async function adminListTenants(): Promise<{
  scope: 'staff' | 'self';
  tenants: Array<{
    id: string;
    slug: string;
    name: string;
    status: string;
    role?: string;
    contactEmail?: string | null;
    programCount?: number;
    userCount?: number;
  }>;
}> {
  return await apiFetch('/api/v1/admin/tenants');
}
